import React from 'react'
import Icon, { IconType } from '@bt-react/components/Icon'
import Textarea from '@bt-react/components/Textarea'
import SidePanelFramework from '@bt-react/components/SidePanelFramework'
import componentStyles from './PresentersChatPanel.module.scss'
import ChatManager from '@bt-react/libs/chatManager'
import ChatMessage from '../ChatMessage'
import EmptySidePanel from '@bt-react/components/EmptySidePanel'
import { PresenterChatPanelProps, PresenterChatPanelState } from './types'
import username from '../../lib/username'
import { addEntry } from '../../lib/audit'

const OLD_MESSAGES_LIMIT = 1000

class PresentersChatPanel extends React.Component<PresenterChatPanelProps, PresenterChatPanelState> {

  containerRef = React.createRef<HTMLDivElement>()
  state: PresenterChatPanelState = {
    messages: [],
    message: '',
    typing: [],
    chat: null,
    BTID: '',
    hasJoinedChannel: false
  }

  componentDidMount () {

    if (this.props.username) this.setState({ BTID: username.getPureUsername(this.props.username) })

    if (this.props.token && this.props.username && this.props.conversationName && !this.state.hasJoinedChannel) this.initiateConnection(this.props.token, this.props.conversationName)

  }

  componentDidUpdate (prevProps, prevState) {

    if (prevProps.username !== this.props.username && this.props.username) this.setState({ BTID: username.getPureUsername(this.props.username) })

    if (prevProps.isPanelActive !== this.props.isPanelActive) this.setReadMessages()

    if (prevState.messages.length < this.state.messages.length && this.containerRef.current) {

      this.containerRef.current.scrollTop = this.containerRef.current.scrollHeight

    }

  }

  initiateConnection = async (token: string, conversationName: string) => {

    let chat

    try {

      chat = await ChatManager.create(token)

    }
    catch (e) {

      addEntry('PresenterService', 'PresenterChat', `Chat client error: ${e}`)

    }

    if (!chat) return

    chat.on('messageAdded', (message) => {

      if (message.conversationName === conversationName) {

        if (this.props.isPanelActive) {

          this.setReadMessages()

        }
        else {

          this.props.newMessageReceived()

        }

        const author = this.getAuthorName(message.author)

        this.setState({
          messages: [...this.state.messages, { ...message, author }]
        })
      }
    })

    chat.on('typingStarted', (typingData) => {

      if (typingData.conversationName === this.props.conversationName) {

        const author = this.getAuthorName(typingData.author)

        this.setState({
          typing: [...this.state.typing, author]
        })
      }
    })

    chat.on('typingEnded', (typingData) => {

      if (typingData.conversationName === this.props.conversationName) {

        const author = this.getAuthorName(typingData.author)

        this.setState({
          typing: this.state.typing.filter((entity) => entity !== author)
        })
      }
    })

    this.setState({
      chat
    })

    let result = false

    try {

      result = await chat.joinConversation(conversationName)
      if (result) this.setState({ hasJoinedChannel: true })
      else {
        result = await chat.createNewConversation(this.props.roomname, this.props.BTID)
        console.log('here is the result of creating a new one :', result)
        if (result) this.setState({ hasJoinedChannel: true })
      }

    }
    catch (e) {

      addEntry('PresenterService', 'PresenterChat', `Failed to join conversation in presenter chat panel. User ID: ${this.props.username}. Conversation name: ${conversationName}`)

    }

    if (!result) return

    const oldMessages = await chat.getMessages(conversationName, OLD_MESSAGES_LIMIT)

    await this.getUnreadMessages(chat)

    this.setState({
      messages: oldMessages.map(({ state }) => ({ ...state, author: this.getAuthorName(state.author) }))
    })

  }

  getAuthorName = (BTID: string): string => {

    BTID = username.getPureUsername(BTID)
    const { participantInformation = new Map() } = this.props
    const participant = participantInformation.get(BTID) || {}
    return `${participant.firstName || ''} ${participant.lastName || ''}`.trim()

  }

  getUnreadMessages = async (chat) => {

    let numMessages = 0
    let numReadMessages = 0

    try {

      numMessages = await chat.getNumMessages(this.props.conversationName, OLD_MESSAGES_LIMIT)

      /**
       * The below uses the username rather than BTID because messages are stored against
       * the identity set in the Twilio token, so sending the BTID would show the Observer
       * the number of unread messages since their last consumed message as a Presenter
       */
      numReadMessages = await chat.getNumReadMessages(this.props.conversationName, this.props.username)

    }
    catch (e) {

      addEntry('PresenterService', 'PresenterChat', `Error getting unread messages in presenter chant panel: ${e}`)

    }

    if (this.props.setChatMessageCounter) {

      const unreadNotifications = numMessages - numReadMessages

      if (unreadNotifications > 0) this.props.setChatMessageCounter(unreadNotifications)
      if (unreadNotifications === 0) this.props.resetChatMessageCounter()

    }

  }

  setReadMessages = async () => {

    if (!this.props.isPanelActive || !this.state.chat) return null

    try {

      const { setMessagesRead } = this.state.chat || {}
      if (setMessagesRead) await setMessagesRead(this.props.conversationName)

    }
    catch (e) {

      addEntry('PresenterService', 'PresenterChat', `Error setting read messages in presenter chant panel: ${e}`)
      return null

    }

    try {

      await this.getUnreadMessages(this.state.chat)
      return true

    }
    catch (e) {

      return null

    }

  }

  renderMessages = () =>
    this.state.messages.map(({ author, body, attributes }, index) =>
      <ChatMessage
        key={index}
        author={author}
        message={body}
        mine={attributes.userId === this.state.BTID}
      />
    )

  onChange = (id, message) => {

    const { typing } = this.state.chat || {}
    if (typing) typing(this.props.conversationName)
    this.setState({
      message
    })

  }

  sendMessage = async () => {

    if (!this.state.chat || !this.props.conversationName) return

    const { conversationName } = this.props
    const message = this.state.message.trim()

    if (message.length) {

      this.setState({
        message: ''
      })
      const { sendMessage } = this.state.chat || {}
      if (sendMessage) await sendMessage(conversationName, message, { userId: this.state.BTID })
    }
    else {

      this.setState({ message: '' })

    }

  }

  onKeyPress = ev => {

    if (ev.which === 13) {

      ev.preventDefault()
      this.sendMessage()

    }

  }

  getTyping = (): JSX.Element => {

    const typingArray = this.state.typing
      .filter((author, index, self) => self.indexOf(author) === index)
    const authorList = typingArray.join(', ')
    const linkingVerb = typingArray.length > 1 ? 'are' : 'is'

    return <i>{`${authorList} ${linkingVerb} typing...`}</i>

  }

  emptyChatbox = () => {

    return (
      <EmptySidePanel
        data-bdd='empty-chat-'
        icon={IconType.iconName.chat}
        title='No chats yet'
        text={
          <div data-bdd='empty-chat-start'>
              Start chatting with your co-presenters below
            <div
              data-bdd='chat-support-note'
              className={componentStyles['empty-chat-note']}
            >
              Note: For BrightTALK Support use the<br/>
              <span>
                <Icon
                  name={IconType.iconName.support}
                  className={componentStyles['support-icon']}
                />
              </span>
              button
            </div>
          </div>
        }
      />
    )

  }

  render () {

    return (
      <SidePanelFramework
        icon={IconType.iconName.chat}
        title="Co-presenter chat"
        contentPadding={false}
        clearBottom={false}
      >
        <div data-bdd='chat-panel' className={componentStyles['chat-panel']}>
          <div data-bdd='chat-box' ref={this.containerRef} className={componentStyles['messages-container']}>
            {
              this.state.messages.length
                ? this.renderMessages()
                : this.emptyChatbox()
            }
          </div>
          <div data-bdd='typing-indicator' className={componentStyles['typing-indicator']}>
            {
              this.state.typing.length
                ? this.getTyping()
                : ''
            }
          </div>
          <div className={componentStyles['chat-input-holder']}>
            <Textarea dataBdd='chat-input' disabled={!this.state.hasJoinedChannel}
              className={componentStyles['chat-input']} placeholder='Type your message here...'
              onKeyPress={this.onKeyPress} onChange={this.onChange} value={this.state.message}
              id='chat-input' name='chat-input'
            />
            <button data-bdd='chat-input-button' disabled={!this.state.hasJoinedChannel}
              onClick={this.sendMessage} className={componentStyles['send-icon']}>
              <Icon name={IconType.iconName.paperPlane} />
            </button>
          </div>
        </div>
      </SidePanelFramework>
    )

  }

}

export default PresentersChatPanel
